import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { MeterForm } from "../meter-form";
import { UploadTab } from "../upload-tab";
import { AddedMeters } from "../added-meters";
import { AddEditMeterModal } from "../add-edit-meter-modal";
import { BulkLineItem } from "../../../../../model/bulk-purchase/bulk-purchase-customer";
import { BulkTab } from "../../bulk-purchase.types";
import styles from "./bulk-entry.module.scss";

interface BulkEntryProps {
	items: BulkLineItem[];
	onAddItem: (item: BulkLineItem) => void;
	onUpdateItem: (item: BulkLineItem) => void;
	onDeleteItem: (id: string) => void;
	onUploadResult: (items: BulkLineItem[], errors: string[]) => void;
	onContinue: () => void;
	continueLabel: string;
	isSubmitting: boolean;
	topLevelErrors: string[];
	currency: string;
}

export const BulkEntry: React.FC<BulkEntryProps> = ({
	items,
	onAddItem,
	onUpdateItem,
	onDeleteItem,
	onUploadResult,
	onContinue,
	continueLabel,
	isSubmitting,
	topLevelErrors,
	currency,
}) => {
	const { t } = useTranslation();
	const [tab, setTab] = useState<BulkTab>("manual");
	const [formKey, setFormKey] = useState(0);
	const [editing, setEditing] = useState<BulkLineItem | null>(null);

	const handleAdd = (item: BulkLineItem) => {
		onAddItem(item);
		setFormKey(k => k + 1); // remount the form so it clears for the next entry
	};

	const handleSaveEdit = (item: BulkLineItem) => {
		onUpdateItem(item);
		setEditing(null);
	};

	return (
		<div className={styles.container}>
			<div className={styles.card}>
				<p className={styles.cardLabel}>{t("buyElectricity.bulk.selectMeterType")}</p>
				<div className={styles.tabs}>
					<button
						type="button"
						className={`${styles.tab} ${tab === "manual" ? styles.tabActive : ""}`}
						onClick={() => setTab("manual")}
					>
						{t("buyElectricity.bulk.manualEntry")}
					</button>
					<button
						type="button"
						className={`${styles.tab} ${tab === "upload" ? styles.tabActive : ""}`}
						onClick={() => setTab("upload")}
					>
						{t("buyElectricity.bulk.uploadXlsx")}
					</button>
				</div>

				<div className={styles.tabBody}>
					{tab === "manual" ? (
						<MeterForm
							key={formKey}
							submitLabel={t("buyElectricity.bulk.addMeter")}
							onSubmit={handleAdd}
							currency={currency}
						/>
					) : (
						<UploadTab onValidated={onUploadResult} />
					)}
				</div>
			</div>

			{topLevelErrors.length > 0 && (
				<div className={styles.errorBanner}>
					{topLevelErrors.map((e, i) => (
						<p key={i}>{e}</p>
					))}
				</div>
			)}

			<AddedMeters items={items} onEdit={setEditing} onDelete={onDeleteItem} currency={currency} />

			<div className={styles.footer}>
				<Button variant="primary" fullWidth className={styles.primaryAction} disabled={items.length === 0 || isSubmitting} onClick={onContinue}>
					{isSubmitting ? t("buyElectricity.bulk.validating") : continueLabel}
				</Button>
			</div>

			{editing && (
				<AddEditMeterModal item={editing} onSave={handleSaveEdit} onClose={() => setEditing(null)} currency={currency} />
			)}
		</div>
	);
};

export default BulkEntry;
